import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@saucelabs/ds-typography';
import { Toggle } from '@saucelabs/ds-inputs';
import { constants as appStorageConstants } from '@saucelabs/sl-module-app-storage';

import SwitchInfoTooltip from './SwitchInfoTooltip';
import { selectors as groupsSelectors } from '../../group';
import selectors from '../selectors';
import operations from '../operations';

const {
  ANDROID,
  IOS,
} = appStorageConstants.APP_STORAGE_KIND;

const TOOLTIP_ANDROID = 'Enables you to capture audio from your app during tests.';
const TOOLTIP_IOS = 'Enables you to capture audio from the iOS/iPadOS devices during tests.';
const TOOLTIP_OTHER = 'n/a';
const SETTING_NAME_ANDROID = 'App Audio';
const SETTING_NAME_IOS = 'OS Audio';
const SETTING_NAME_OTHER = 'n/a';

const Audio: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const settings = useSelector(groupsSelectors.currentGroupSettingsSelector);
  const currentGroup = useSelector(groupsSelectors.currentGroupSelector);
  const isLoading = useSelector(selectors.isSettingsLoadingSelector);
  const kind = useSelector(groupsSelectors.currentGroupKindSelector);
  const currentValue = settings?.audio_capture ?? false;

  const settingsName = useMemo(() => {
    if (kind === ANDROID) {
      return SETTING_NAME_ANDROID;
    }
    if (kind === IOS) {
      return SETTING_NAME_IOS;
    }
    return SETTING_NAME_OTHER;
  }, [kind]);

  const changeHandler = useCallback((newValue: boolean) => {
    if (currentGroup) {
      void dispatch(operations.updateSettings({
        groupId: currentGroup.id,
        originalValues: {
          audio_capture: currentValue,
        },
        settingsName: settingsName.toLowerCase(),
        updatedValues: {
          audio_capture: newValue,
        },
      }));
    }
  }, [currentGroup, dispatch, currentValue, settingsName]);

  const tooltipText = useMemo(() => {
    if (kind === ANDROID) {
      return TOOLTIP_ANDROID;
    }
    if (kind === IOS) {
      return TOOLTIP_IOS;
    }
    return TOOLTIP_OTHER;
  }, [kind]);

  return (
    <div className="flex justify-between">
      <div className="flex">
        <Typography className="text-high-emphasis font-semibold">
          {settingsName}
        </Typography>
        <SwitchInfoTooltip
          title={tooltipText}
        />
      </div>
      <Toggle
        isDisabled={isLoading}
        isSelected={currentValue}
        onChange={changeHandler}
      />
    </div>
  );
};

export default Audio;
